import store from '../store';
import { getMaterialSymbolSvg } from '../utils/lazyMaterialSymbolSvgs.js';
import { resolveHelpCenterUrl } from '../shared/helpCenterResolver';
import { shouldHideCurrencyConverter } from '../utils/hideCurrencyConverter.js';

export function registerWindowApi(storeRef) {
    window.switchCurrency = function (currencyCode) {
        if (shouldHideCurrencyConverter(storeRef.state.lang)) {
            storeRef.dispatch('syncCurrencyToLanguage', { skipCatalogRefresh: true });
            storeRef.dispatch('form/reloadProductsCatalog').catch(() => {});
            return;
        }
        const code = String(currencyCode || '').toUpperCase();
        if (!code) return;
        storeRef.dispatch('currency/setCurrencyByCode', code);
    };

    window.getHelpCenterUrl = () => {
        return resolveHelpCenterUrl(window.APP_LANG);
    };
}

const TERMS_OF_USE_ANCHOR_OFFSET_PX = 90;

function scrollToInPageTarget(target, smooth) {
    if (!target) return;
    requestAnimationFrame(() => {
        const termRoot = document.querySelector('.term-of-use-anchor-offset');
        if (termRoot && termRoot.contains(target)) {
            const y = target.getBoundingClientRect().top + window.scrollY - TERMS_OF_USE_ANCHOR_OFFSET_PX;
            window.scrollTo({ top: Math.max(0, y), behavior: smooth ? 'smooth' : 'auto' });
            return;
        }
        target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    });
}

function scrollTermsOfUseHashFromLocation(smooth) {
    const hash = typeof location !== 'undefined' ? location.hash : '';
    if (!hash || hash === '#') return;
    let target;
    try {
        target = document.querySelector(hash);
    } catch (err) {
        return;
    }
    scrollToInPageTarget(target, smooth);
}

export function installLegacyDomEnhancements() {
    document.addEventListener('DOMContentLoaded', function () {
        requestAnimationFrame(function () {
            scrollTermsOfUseHashFromLocation(false);
        });

        window.addEventListener('hashchange', function () {
            scrollTermsOfUseHashFromLocation(true);
        });

        document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
            anchor.addEventListener('click', function (e) {
                const href = this.getAttribute('href');
                if (href === '#') return;
                e.preventDefault();
                const target = document.querySelector(href);
                scrollToInPageTarget(target, true);
            });
        });
        document.querySelectorAll('form[data-no-submit]').forEach(function (form) {
            form.addEventListener('submit', function (e) {
                e.preventDefault();
            });
        });
    });

    window.UIUtils = window.UIUtils || {};
    window.UIUtils.toggleElement = function (selector) {
        const el = document.querySelector(selector);
        if (el) el.style.display = el.style.display === 'none' ? '' : 'none';
    };

    window.handleFeedback = function (type) {
        const yesBtn = document.getElementById('btn-yes');
        const noBtn = document.getElementById('btn-no');
        const thanks = document.getElementById('feedback-thanks');

        if (!yesBtn || !noBtn || !thanks) return;

        yesBtn.classList.remove('border-teal-400', 'bg-teal-50', 'text-teal-700');
        noBtn.classList.remove('border-red-300', 'bg-red-50', 'text-red-600');

        if (type === 'yes') {
            yesBtn.classList.add('border-teal-400', 'bg-teal-50', 'text-teal-700');
        } else {
            noBtn.classList.add('border-red-300', 'bg-red-50', 'text-red-600');
        }

        thanks.classList.remove('hidden');
    };

    window.updateCharCount = function (inputId, countId, max) {
        const input = document.getElementById(inputId);
        const counter = document.getElementById(countId);
        if (!input || !counter) return;

        const len = input.value.length;
        counter.textContent = `${len} / ${max}`;
        if (len > max * 0.9) {
            counter.classList.add('text-amber-500');
            counter.classList.remove('text-gray-400');
        } else {
            counter.classList.remove('text-amber-500');
            counter.classList.add('text-gray-400');
        }
    };

    window.handleDragOver = function (e) {
        e.preventDefault();
        const zone = document.getElementById('upload-zone');
        if (!zone) return;
        zone.style.borderColor = '#1e3a8a';
        zone.style.backgroundColor = '#eff6ff';
    };

    window.handleDragLeave = function () {
        const zone = document.getElementById('upload-zone');
        if (!zone) return;
        zone.style.borderColor = '#d1d5db';
        zone.style.backgroundColor = '';
    };

    /**
     * Inline SVG from /img/svg/{slug}.svg, same files Blade uses.
     * Async so the file-upload list does not pull the full icon atlas.
     */
    window.materialSymbolMaskEmbed = async function materialSymbolMaskEmbed(iconName, extraClass) {
        const svg = await getMaterialSymbolSvg(iconName);
        const cls = (extraClass || '').trim();

        return (
            `<span class="material-symbol-embed material-symbol-slot inline-flex items-center justify-center align-middle ${cls}" aria-hidden="true">${svg}</span>`
        );
    };

    window.renderFileList = async function (files) {
        const list = document.getElementById('file-list');
        if (!list) return;

        list.innerHTML = '';
        if (!files || files.length === 0) {
            list.classList.add('hidden');
            return;
        }

        list.classList.remove('hidden');
        for (const file of Array.from(files)) {
            const parts = String(file.name || '').split('.');
            const ext = (parts.length > 1 ? parts.pop() : '').toUpperCase();
            const size = `${(file.size / 1024).toFixed(0)} KB`;
            const item = document.createElement('div');
            item.className = 'flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-small px-4 py-2.5';
            const icon = await window.materialSymbolMaskEmbed('attach_file', 'text-blue-900 text-base flex-shrink-0');
            item.innerHTML =
                icon +
                `<span class="text-sm font-medium text-gray-800 flex-1 truncate">${file.name}</span>` +
                `<span class="text-xs text-gray-400 flex-shrink-0">${size}</span>` +
                `<span class="text-xs font-bold text-blue-900 bg-blue-50 border border-blue-100 rounded-small px-2 py-0.5 flex-shrink-0">${ext}</span>`;
            list.appendChild(item);
        }
    };

    window.handleDrop = function (e) {
        e.preventDefault();
        window.handleDragLeave();
        const files = e?.dataTransfer?.files;
        window.renderFileList(files);
    };

    window.handleFileSelect = function (e) {
        window.renderFileList(e?.target?.files);
    };

    document.addEventListener('DOMContentLoaded', function () {
        const consent = document.getElementById('consent');
        if (!consent) return;

        consent.addEventListener('change', function () {
            const icon = document.getElementById('check-icon');
            if (!icon) return;
            if (this.checked) icon.classList.remove('hidden');
            else icon.classList.add('hidden');
        });
    });
}
